/**
 * Hnefatafl（维京板棋）棋盘组件
 * 11×11棋盘，不对称对抗游戏
 * 进攻方24枚棋子 vs 防守方13枚棋子（1国王+12兵卒）
 */

import React from 'react';
import { HnefataflGameState, Position, HnefataflPiece, HnefataflPieceType } from '../types/game';
import { HNEFATAFL_PIECE_EMOJIS, HNEFATAFL_PIECE_SYMBOLS } from '../types/game';
import { cn } from '../lib/utils';

interface HnefataflBoardProps {
  gameState: HnefataflGameState;
  onCellClick: (position: Position) => void;
  disabled?: boolean;
  enlarged?: boolean;
  gameMode?: 'pvp' | 'pvc';
}

const HnefataflBoard: React.FC<HnefataflBoardProps> = ({
  gameState,
  onCellClick,
  disabled = false,
  enlarged = false,
  gameMode = 'pvp'
}) => {
  const board = gameState.board;

  // 获取格子尺寸（优化移动端适配）
  const getCellSize = (): string => {
    return enlarged
      ? 'w-full h-full aspect-square' // 使用相对尺寸，确保不会超出容器
      : 'w-full h-full aspect-square'; // 使用相对尺寸，确保不会超出容器
  };

  // 获取棋子尺寸（优化移动端适配）
  const getPieceSize = (): string => {
    return enlarged
      ? 'w-[85%] h-[85%]' // 使用百分比尺寸，相对于格子
      : 'w-[80%] h-[80%]'; // 使用百分比尺寸，相对于格子
  };

  // 判断是否为禁止位置（中心和四个角落）
  const isForbiddenPosition = (row: number, col: number): boolean => {
    // 中心位置
    if (row === 5 && col === 5) return true;

    // 四个角落
    if ((row === 0 && col === 0) ||
        (row === 0 && col === 10) ||
        (row === 10 && col === 0) ||
        (row === 10 && col === 10)) return true;

    return false;
  };

  // 判断是否为角落位置
  const isCorner = (row: number, col: number): boolean => {
    return (row === 0 && col === 0) ||
           (row === 0 && col === 10) ||
           (row === 10 && col === 0) ||
           (row === 10 && col === 10);
  };

  // 判断是否为选中的棋子
  const isSelected = (row: number, col: number): boolean => {
    return gameState.selectedPiece?.row === row && gameState.selectedPiece?.col === col;
  };

  // 判断是否为可能的移动位置
  const isPossibleMove = (row: number, col: number): boolean => {
    return gameState.possibleMoves?.some(move => move.to.row === row && move.to.col === col) || false;
  };

  // 判断是否为吃子移动
  const isCaptureMove = (row: number, col: number): boolean => {
    const move = gameState.possibleMoves?.find(move => move.to.row === row && move.to.col === col);
    return move?.isCapture || false;
  };

  // 获取格子样式
  const getCellStyle = (row: number, col: number): string => {
    const baseStyle = getCellSize();
    const isForbidden = isForbiddenPosition(row, col);
    const isSelectedCell = isSelected(row, col);
    const isPossibleMoveCell = isPossibleMove(row, col);
    const isCaptureMoveCell = isCaptureMove(row, col);

    // 基础样式
    let style = `${baseStyle} border border-amber-900 relative transition-all duration-300 hover:shadow-lg`;

    // 根据位置类型设置背景色
    if (isForbidden) {
      // 禁止位置 - 特殊标记（移除闪烁效果）
      if (isCorner) {
        style += ' bg-gradient-to-br from-rose-400 via-red-500 to-red-600 cursor-not-allowed shadow-inner';
      } else {
        style += ' bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 cursor-not-allowed shadow-inner';
      }
    } else {
      // 普通格子 - 棋盘样式，更美观的渐变
      const isDark = (row + col) % 2 === 0;
      if (isDark) {
        style += ' bg-gradient-to-br from-amber-700 via-amber-600 to-yellow-700 hover:from-amber-600 hover:via-amber-500 hover:to-yellow-600 cursor-pointer shadow-sm';
      } else {
        style += ' bg-gradient-to-br from-amber-100 via-yellow-100 to-amber-200 hover:from-yellow-50 hover:via-amber-100 hover:to-yellow-200 cursor-pointer shadow-sm';
      }
    }

    // 选中效果 - 更精美的动画
    if (isSelectedCell) {
      style += ' ring-4 ring-blue-400 ring-opacity-80 shadow-2xl scale-105 z-20 border-blue-500';
    }

    // 可能移动高亮 - 更柔和的颜色
    if (isPossibleMoveCell) {
      if (isCaptureMoveCell) {
        style += ' ring-2 ring-rose-400 ring-opacity-90 bg-gradient-to-br from-rose-200 to-pink-200 animate-pulse border-rose-500';
      } else {
        style += ' ring-2 ring-emerald-400 ring-opacity-80 bg-gradient-to-br from-emerald-200 to-green-200 border-emerald-500';
      }
    }

    // 禁用状态
    if (disabled) {
      style += ' opacity-50 cursor-not-allowed';
    }

    return style;
  };

  // 获取棋子样式（优化移动端适配）
  const getPieceStyle = (piece: HnefataflPiece): string => {
    const baseStyle = `${getPieceSize()} rounded-full shadow-xl transition-all duration-500 border-2 flex items-center justify-center absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 p-0.5 xs:p-1 hover:shadow-2xl hover:scale-105`;

    // 根据玩家和类型设置样式
    if (piece.type === 'king') {
      // 国王样式 - 王者风范，更华丽的渐变
      if (piece.player === 'defender') {
        return `${baseStyle} bg-gradient-to-br from-yellow-300 via-yellow-400 to-amber-600 border-amber-700 shadow-2xl relative z-10 ring-2 ring-amber-400 ring-opacity-50`;
      } else {
        return `${baseStyle} bg-gradient-to-br from-orange-400 via-red-500 to-red-700 border-red-800 shadow-2xl relative z-10 ring-2 ring-red-400 ring-opacity-50`;
      }
    } else {
      // 兵卒样式 - 更精致的设计
      if (piece.player === 'defender') {
        return `${baseStyle} bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-700 border-indigo-800 shadow-xl relative z-10 ring-1 ring-blue-300 ring-opacity-40`;
      } else {
        return `${baseStyle} bg-gradient-to-br from-red-500 via-red-600 to-rose-800 border-rose-900 shadow-xl relative z-10 ring-1 ring-red-300 ring-opacity-40`;
      }
    }
  };

  // 渲染棋子
  const renderPiece = (piece: HnefataflPiece | null, row: number, col: number) => {
    if (!piece) return null;

    const isSelectedCell = isSelected(row, col);
    const isCurrentPlayerPiece = piece.player === gameState.currentPlayer;
    const isKing = piece.type === 'king';

    return (
      <div className={cn(
        getPieceStyle(piece),
        {
          // 选中效果
          'scale-110 animate-pulse': isSelectedCell,
          // 当前玩家棋子发光效果
          'ring-2 ring-opacity-40 ring-offset-1': isCurrentPlayerPiece && !isSelectedCell,
          'ring-blue-300 ring-opacity-30': isCurrentPlayerPiece && piece.player === 'defender' && !isSelectedCell,
          'ring-orange-300 ring-opacity-30': isCurrentPlayerPiece && piece.player === 'attacker' && !isSelectedCell,
          // 对手棋子暗淡效果
          'opacity-85': !isCurrentPlayerPiece
        }
      )}>
        {/* 国王特殊装饰 */}
        {isKing && (
          <>
            {/* 王冠光晕 - 简化相对尺寸 */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {/* 外层光晕 */}
              <div className="absolute w-[140%] h-[140%] bg-gradient-to-r from-yellow-200 via-yellow-300 to-amber-300 rounded-full opacity-30 animate-pulse"></div>
              {/* 中层光晕 */}
              <div className="w-[120%] h-[120%] bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-400 rounded-full opacity-50 animate-spin-slow"></div>
              {/* 内层光晕 */}
              <div className="w-[80%] h-[80%] bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full opacity-70 animate-pulse"></div>
            </div>

            {/* 王冠标记 - 相对尺寸 */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-2xl sm:text-4xl drop-shadow-2xl filter brightness-110 contrast-125 transform hover:scale-110 transition-transform duration-300">
                👑
              </span>
            </div>
          </>
        )}

        {/* 普通棋子标记 */}
        {!isKing && (
          <span className="text-2xl sm:text-4xl select-none drop-shadow-lg filter brightness-110 contrast-110 transform hover:scale-110 transition-transform duration-300">
            {HNEFATAFL_PIECE_EMOJIS[piece.type][piece.player]}
          </span>
        )}

        {/* 棋子光泽效果 - 相对尺寸 */}
        <div className="absolute top-[15%] left-[15%] w-[20%] h-[20%] bg-white rounded-full opacity-50 blur-sm"></div>
        <div className="absolute top-[10%] left-[15%] w-[15%] h-[15%] bg-white rounded-full opacity-70"></div>

        {/* 选中棋子的额外指示器 */}
        {isSelectedCell && (
          <div className="absolute -inset-1 rounded-full border-2 border-blue-400 animate-ping"></div>
        )}

        {/* 最后一步标记 */}
        {gameState.moves.length > 0 &&
         gameState.moves[gameState.moves.length - 1].to.row === row &&
         gameState.moves[gameState.moves.length - 1].to.col === col && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className={`${
              enlarged
                ? 'w-4 h-4 xs:w-5 xs:h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 xl:w-9 xl:h-9'
                : 'w-3 h-3 xs:w-4 xs:h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8'
            } bg-purple-400 rounded-full opacity-80 animate-ping`}></div>
          </div>
        )}
      </div>
    );
  };

  // 渲染移动指示器
  const renderMoveIndicator = (row: number, col: number) => {
    if (!isPossibleMove(row, col)) return null;

    const isCapture = isCaptureMove(row, col);

    return (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
        <div
          className={cn(
            "rounded-full border-2 animate-pulse shadow-xl backdrop-blur-sm w-[70%] h-[70%]",
            isCapture
              ? "bg-gradient-to-br from-rose-500 to-red-600 border-rose-700 opacity-90 animate-bounce"
              : "bg-gradient-to-br from-emerald-500 to-green-600 border-emerald-700 opacity-85"
          )}
        >
          {/* 添加图标指示 */}
          <div className="flex items-center justify-center text-white font-bold drop-shadow-lg text-2xl sm:text-3xl">
            {isCapture ? '⚔️' : '✓'}
          </div>
        </div>

        {/* 外圈动画效果 - 精美波纹 */}
        <div
          className={cn(
            "absolute rounded-full border-2 animate-ping pointer-events-none w-[100%] h-[100%]",
            isCapture
              ? "border-rose-300"
              : "border-emerald-300"
          )}
        />
      </div>
    );
  };

  // 渲染坐标标记
  const renderCoordinates = () => {
    // 行号 (0-10)
    const rowNumbers = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0];
    // 列号 (A-K)
    const colLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'];

    return (
      <div className="absolute -top-6 -left-6 text-xs text-gray-600 font-mono">
        {/* 行号 */}
        <div className="flex flex-col justify-between h-full">
          {rowNumbers.map((num, index) => (
            <div key={num} className="flex items-center justify-center w-6 h-full">
              {num}
            </div>
          ))}
        </div>
        {/* 列号 */}
        <div className="flex justify-between w-full -top-6 absolute">
          {colLetters.map((letter, index) => (
            <div key={letter} className="flex items-center justify-center w-full h-6">
              {letter}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 获取最后一步位置
  const getLastMovePosition = (): Position | null => {
    if (gameState.moves.length === 0) return null;
    const lastMove = gameState.moves[gameState.moves.length - 1];
    return lastMove.to;
  };

  const lastMovePos = getLastMovePosition();

  return (
    <div className="flex p-2 xs:p-4 lg:p-8">
      {/* 棋盘 - 移动端全屏，桌面端优化尺寸 */}
      <div className={`
        ${enlarged ? 'flex-1 xs:flex-none sm:flex-none md:flex-none lg:flex-none xl:flex-none' : 'flex-1 xs:flex-none sm:flex-none md:flex-none lg:flex-none xl:flex-none'}
        relative bg-amber-500 p-1 xs:p-2 sm:p-3 md:p-4 lg:p-5 xl:p-6 rounded-xl shadow-2xl overflow-hidden
        max-w-[700px] max-h-[700px] 
        w-full h-full
        flex items-center justify-center mx-auto
      `}>
        <div className={`
          grid grid-cols-11 gap-0 border-4 xs:border-4 sm:border-5 md:border-6 lg:border-6 xl:border-6 2xl:border-6 border-amber-900 rounded-xl relative
          xs:w-full xs:h-full xs:max-w-full xs:max-h-full
          sm:w-full sm:h-full sm:max-w-full sm:max-h-full
          md:w-full md:h-full md:max-w-full md:max-h-full
          lg:w-full lg:h-full lg:max-w-full lg:max-h-full
          xl:w-full xl:h-full xl:max-w-full xl:max-h-full
          2xl:w-full 2xl:h-full 2xl:max-w-full 2xl:max-h-full
          mx-auto shadow-2xl
          aspect-square
          max-w-[600px] sm:max-w-[700px] md:max-w-[850px] lg:max-w-[950px] xl:max-w-[1000px] 2xl:max-w-[1100px]
          max-h-[600px] sm:max-h-[700px] md:max-h-[850px] lg:max-h-[950px] xl:max-h-[1000px] 2xl:max-h-[1100px]
        `}>
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={cn(
                  getCellStyle(rowIndex, colIndex),
                  {
                    "cursor-not-allowed": disabled
                  }
                )}
                onClick={() => {
                  if (disabled) return;
                  console.log('HnefataflBoard: Click detected at', rowIndex, colIndex);
                  onCellClick({ row: rowIndex, col: colIndex });
                }}
              >
                {/* 棋子 */}
                {renderPiece(cell, rowIndex, colIndex)}

                {/* 移动指示器 */}
                {renderMoveIndicator(rowIndex, colIndex)}

                {/* 最后一步标记 */}
                {lastMovePos && lastMovePos.row === rowIndex && lastMovePos.col === colIndex && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-[30%] h-[30%] bg-purple-400 rounded-full opacity-80 animate-ping"></div>
                  </div>
                )}

                {/* 禁止位置标记（移除闪烁效果） */}
                {isForbiddenPosition(rowIndex, colIndex) && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    {isCorner(rowIndex, colIndex) ? (
                      <span className="text-2xl sm:text-4xl opacity-70">
                        🏰
                      </span>
                    ) : (
                      <span className="text-2xl sm:text-4xl opacity-60">
                        ⚠️
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* 坐标标记 */}
        {renderCoordinates()}
      </div>

      {/* 游戏规则说明 - 桌面版显示，移动端简化 */}
      <div className="mt-4 hidden xs:block bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl shadow-lg border border-amber-200 p-3 xs:p-5 sm:p-6 max-w-4xl w-full mx-auto">
        <h3 className="text-base xs:text-lg font-bold text-amber-900 mb-3 xs:mb-4 text-center">
          🛡️ Hnefatafl（维京板棋）规则
        </h3>

        <div className="grid md:grid-cols-2 gap-4 xs:gap-6 text-xs xs:text-sm">
          {/* 基本规则 */}
          <div>
            <h4 className="font-semibold text-amber-800 mb-2 xs:mb-3 flex items-center">
              <span className="mr-1 xs:mr-2 text-sm xs:text-base">🎯</span>
              <span className="text-sm xs:text-base">基本规则</span>
            </h4>
            <ul className="space-y-1 xs:space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="mr-1 xs:mr-2 text-amber-600 text-xs xs:text-sm">•</span>
                <span className="text-xs xs:text-sm"><strong>棋盘：</strong>11×11格，中心格和四角禁止停留</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1 xs:mr-2 text-amber-600 text-xs xs:text-sm">•</span>
                <span className="text-xs xs:text-sm"><strong>移动方式：</strong>所有棋子纵横移动任意格数</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1 xs:mr-2 text-amber-600 text-xs xs:text-sm">•</span>
                <span className="text-xs xs:text-sm"><strong>夹吃规则：</strong>两枚己方棋子夹住敌方棋子时吃掉</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1 xs:mr-2 text-amber-600 text-xs xs:text-sm">•</span>
                <span className="text-xs xs:text-sm"><strong>国王格夹吃：</strong>被挡在国王格中可直接夹吃</span>
              </li>
            </ul>
          </div>

          {/* 获胜条件 */}
          <div>
            <h4 className="font-semibold text-amber-800 mb-2 xs:mb-3 flex items-center">
              <span className="mr-1 xs:mr-2 text-sm xs:text-base">🏆</span>
              <span className="text-sm xs:text-base">获胜条件</span>
            </h4>
            <ul className="space-y-1 xs:space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="mr-1 xs:mr-2 text-blue-600 font-bold text-xs xs:text-sm">🛡️</span>
                <span className="text-xs xs:text-sm"><strong>防守方：</strong>国王到达四角任一位置获胜</span>
              </li>
              <li className="flex items-start">
                <span className="mr-1 xs:mr-2 text-red-600 font-bold text-xs xs:text-sm">⚔</span>
                <span className="text-xs xs:text-sm"><strong>进攻方：</strong>围堵国王使其无法移动获胜</span>
              </li>
            </ul>
          </div>
        </div>

        {/* 策略提示 */}
        <div className="mt-4 xs:mt-6 pt-3 xs:pt-4 border-t border-amber-200">
          <div className="text-xs text-amber-700 text-center">
            <span className="font-semibold">💡 策略提示：</span>
            <span className="hidden xs:inline">防守方控制中心，利用夹吃削弱进攻力量。进攻方形成包围网，逐步限制国王活动空间。</span>
            <span className="xs:hidden">控制中心，夹吃削弱对手</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HnefataflBoard;